import { useState } from "react";
import { Calendar, LayoutDashboard, MessageSquare, Play, TrendingUp } from "lucide-react";
import { BRAND, ButtonLink, Eyebrow, INK, Reveal, TINT, WRAP } from "./ui";

/* YouTube video ID of the demo. It is the part after "v=" in the video link,
   for example youtube.com/watch?v=THIS_PART */
const YOUTUBE_VIDEO_ID = "_zShxJofkjU";

const YOUTUBE_EMBED_URL = `https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?controls=1&modestbranding=1&rel=0`;
const YOUTUBE_THUMBNAIL_URL = `https://img.youtube.com/vi/${YOUTUBE_VIDEO_ID}/hqdefault.jpg`;

/* The workflows the demo covers, listed next to the video. */
const WORKFLOWS = [
  {
    id: "pipeline",
    icon: TrendingUp,
    label: "Managing the sales pipeline",
    text: "Move companies across eight stages, verify new prospects and close deals as won or lost.",
  },
  {
    id: "interactions",
    icon: MessageSquare,
    label: "Recording customer interactions",
    text: "Log calls, emails, meetings, notes and field work so the whole story sits in one timeline.",
  },
  {
    id: "tasks",
    icon: Calendar,
    label: "Managing tasks and follow-ups",
    text: "Create tasks with a priority, due date and assignee so no follow-up slips through.",
  },
  {
    id: "dashboard",
    icon: LayoutDashboard,
    label: "Monitoring sales performance",
    text: "See revenue, active deals, tasks due today and pipeline value the moment you log in.",
  },
];

/* Click-to-play YouTube player: the video only loads once the user presses play. */
function DemoVideo() {
  const [playing, setPlaying] = useState(false);

  return (
    <div className="relative overflow-hidden shadow-2xl rounded-2xl" style={{ background: INK }}>
      <div className="relative flex items-center justify-center aspect-video">
        {playing ? (
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`${YOUTUBE_EMBED_URL}&autoplay=1`}
            title="IZSales in Action"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
          />
        ) : (
          <>
            {YOUTUBE_VIDEO_ID && (
              <div
                aria-hidden="true"
                className="absolute inset-0 bg-center bg-cover opacity-60"
                style={{ backgroundImage: `url(${YOUTUBE_THUMBNAIL_URL})` }}
              />
            )}
            <button
              type="button"
              onClick={() => setPlaying(true)}
              aria-label="Play the IZSales in Action video"
              className="relative rounded-full group focus:outline-none focus-visible:ring-4 focus-visible:ring-cyan-300/60"
            >
              <span
                aria-hidden="true"
                className="absolute inset-0 transition-transform duration-300 scale-110 rounded-full bg-white/15 group-hover:scale-125"
              />
              <span className="relative flex items-center justify-center w-20 h-20 bg-white rounded-full shadow-2xl">
                <Play size={32} className="ml-1" style={{ color: BRAND }} />
              </span>
            </button>
            <div className="absolute left-0 bottom-4">
              <div className="px-4 py-3 text-white rounded-r-lg bg-black/50 backdrop-blur-sm">
                <p className="font-semibold">IZSales in Action</p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function InAction() {
  return (
    <section id="in-action" className="relative py-24 overflow-hidden scroll-mt-24" style={{ background: TINT }}>
      <div className={`${WRAP} relative`}>
        <Reveal>
          <Eyebrow>Product tour</Eyebrow>
          <h2 className="mt-5 text-3xl font-extrabold tracking-tight text-[#061A21] sm:text-4xl lg:text-[44px] lg:leading-[1.1]">
            IZSales in Action
          </h2>
          <p className="max-w-2xl mt-4 text-[17px] leading-relaxed text-slate-700">
            Watch how IZSales brings your whole sales workflow together, from the first company you add to the deal
            you close.
          </p>
        </Reveal>

        <div className="grid items-center gap-10 mt-12 lg:grid-cols-12 lg:gap-14">
          <Reveal className="lg:col-span-7">
            <DemoVideo />
          </Reveal>

          <Reveal delay={150} className="lg:col-span-5">
            <h3 className="text-[20px] font-bold text-[#061A21]">What you will see in this demo:</h3>
            <ul className="mt-5 space-y-3">
              {WORKFLOWS.map(({ id, icon: Icon, label, text }) => (
                <li key={id} className="flex items-start gap-4 p-4 bg-white border rounded-xl border-slate-300">
                  <span
                    className="flex items-center justify-center flex-shrink-0 w-10 h-10 text-white rounded-lg"
                    style={{ background: BRAND }}
                  >
                    <Icon size={18} />
                  </span>
                  <span className="block">
                    <span className="block text-[16px] font-bold text-[#061A21]">{label}</span>
                    <span className="block mt-1 text-[14px] leading-relaxed text-slate-600">{text}</span>
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <ButtonLink href="#features">Explore all features</ButtonLink>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}